import Analytics from "@/shared/utils/tagManagerAnalytics";
import { PDF_GENERATION_ERROR_MESSAGE } from "@constants/index";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldProgressSpinner } from "@emerald-react/progress-indicator";
import { useCloseShareModalOnMapAction } from "@hooks/useCloseShareModalOnMapAction";
import { useMediaFiles } from "@hooks/useSurveySelectors";
import useSurveyState from "@hooks/useSurveyState";
import {
  formatSurveyUpdateDate,
  useSurveyPDF,
} from "@survey/utils/SharePDFUtils";
import { useRef, useState } from "react";

const SharePdfSection = ({ isOpen }: { isOpen: boolean }) => {
  const { survey } = useSurveyState();
  const mediaFiles = useMediaFiles();
  const [isCopying, setIsCopying] = useState(false);
  const shouldPreventAutoTriggerRef = useRef(false);

  // Prevent auto-trigger when user interacts with builder-main or opens properties drawer
  useCloseShareModalOnMapAction(shouldPreventAutoTriggerRef);

  // PDF workflow management
  const {
    status,
    pdfUrl,
    pdfCreatedOn,
    errorMessage,
    handlePdfAction,
    isPdfDownloadReady,
  } = useSurveyPDF({
    surveyId: survey?.id || "",
    surveyUpdatedOn: survey?.updatedOn,
    isOpen,
    mediaFiles: mediaFiles || undefined,
    survey: survey || undefined,
    shouldPreventAutoTriggerRef,
  });

  // Handle "Copy PDF link" - only available once a PDF has been generated
  const handleCopyClick = async () => {
    if (!isPdfDownloadReady || !pdfUrl || !survey) return;

    // Find section indices safely
    const marketMapIndex =
      survey.sections?.findIndex((s) =>
        s.name?.toLowerCase().includes("marketmap"),
      ) ?? -1;
    const propertyComparisonIndex =
      survey.sections?.findIndex((s) =>
        s.name?.toLowerCase().includes("comparison"),
      ) ?? -1;
    const propertyIndex =
      survey.sections?.findIndex((s) =>
        s.name?.toLowerCase().includes("properties"),
      ) ?? -1;
    const tourScheduleIndex =
      survey.sections?.findIndex((s) =>
        s.name?.toLowerCase().includes("tour"),
      ) ?? -1;

    // Check if properties have custom fields
    const hasCustomFields =
      survey.properties?.some(
        (property) =>
          property.customFields && property.customFields.length > 0,
      ) ?? false;

    // Get active property list
    const activePropertyList =
      survey.properties?.filter((property) => !property.isHidden).length ?? 0;

    Analytics.send({
      event: "surveyDownloadClick",
      SurveyID: survey.id,
      SurveyName: survey.name,
      Cover: survey.sections?.[0] ? !survey.sections[0].isHidden : false,
      BackgroundImage: survey.coverBackgroundImage?.displayName || "",
      SurveyTitle: survey.coverTitle || "",
      SurveySubtitle: survey.coverSubTitle || "",
      ClientName: survey.clientName || "",
      MarketMap:
        marketMapIndex >= 0
          ? !survey.sections[marketMapIndex].isHidden
          : false,
      PropertyComparison:
        propertyComparisonIndex >= 0
          ? !survey.sections[propertyComparisonIndex].isHidden
          : false,
      Properties:
        propertyIndex >= 0 ? !survey.sections[propertyIndex].isHidden : false,
      TourSchedule:
        tourScheduleIndex >= 0
          ? !survey.sections[tourScheduleIndex].isHidden
          : false,
      NumberOfProperties: survey.properties?.length || 0,
      PropertyCustomFields: hasCustomFields,
      ProjectID: survey.projectId || "",
      TeamID: survey.teamId || "",
      SurveyCreationDate: survey.createdOn || "",
      Origin: survey.origin || "",
      ProjectCreationDate: survey.projectCreatedAt || "",
      SiteIQUserID: survey.createdBy || "", // Use createdBy instead of user.userIdList
      CustomeLegends: survey.customLegends || "",
      ShortList: survey.isClientShortlist || false,
      Collaborator: survey.isCollaborator || false,
      SubjectProperties: activePropertyList,
    });
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(`Property PDF Survey: ${pdfUrl}`);
    } catch (_err) {
      console.error("Failed to copy to clipboard");
    } finally {
      setTimeout(() => setIsCopying(false), 3000);
    }
  };

  // Handle "Generate/Regenerate PDF link" - always available, regardless of survey edits
  const handleGenerateClick = () => {
    Analytics.send({ DownloadFromLocation: "Builder" });
    handlePdfAction(true);
  };

  // Consolidated UI state management
  const isLoading = status === "loading" || status === "generating";
  const hasPdf = isPdfDownloadReady;
  const isUpToDate = status === "available";
  const copyButtonLabel = isCopying ? "Copied!" : "Copy PDF link";
  const generateButtonLabel = hasPdf ? "Regenerate PDF link" : "Generate PDF link";

  const formattedLastModified = survey?.updatedOn
    ? formatSurveyUpdateDate(survey.updatedOn)
    : null;
  const formattedPdfGenerated = pdfCreatedOn
    ? formatSurveyUpdateDate(pdfCreatedOn)
    : null;

  return (
    <div className="border border-divider-1 rounded p-4 bg-share-section flex flex-col gap-y-3">
      {/* AC4: Last modified date is always retained */}
      {formattedLastModified && (
        <p className="text-sm flex items-center gap-1">
          <EmeraldIcon
            icon="schedule"
            variant="icon"
            fontsizeInRem={0.9}
            style={{ cursor: "default" }}
          />
          Last modified: {formattedLastModified.date} at{" "}
          {formattedLastModified.time}
        </p>
      )}

      {/* AC5: PDF generated date is shown once a PDF exists */}
      {hasPdf && formattedPdfGenerated && (
        <p className="text-sm flex items-center gap-1">
          <EmeraldIcon
            icon="description"
            variant="icon"
            fontsizeInRem={0.9}
            style={{ cursor: "default" }}
          />
          PDF generated: {formattedPdfGenerated.date} at{" "}
          {formattedPdfGenerated.time}
        </p>
      )}

      {/* "Up to date" badge sits on its own line, centered below the dates */}
      {hasPdf && isUpToDate && (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1 text-xs font-medium text-success bg-success-alert rounded-full px-3 py-1">
            <EmeraldIcon icon="check_circle" variant="icon" fontsizeInRem={0.85} />
            Up to date
          </span>
        </div>
      )}

      {/* AC2/AC3: Copy PDF Link - active when a PDF exists, greyed out otherwise */}
      <EmeraldButton
        className="w-full"
        onClick={handleCopyClick}
        disabled={!hasPdf || isCopying}
      >
        {copyButtonLabel}
      </EmeraldButton>
      {hasPdf ? (
        <p className="text-sm text-gray-900">
          Copy your PDF link to share it directly with your client.
        </p>
      ) : (
        <p className="text-sm text-gray-900">No PDF generated yet</p>
      )}

      {/* AC1/AC7: Generate PDF is always visible and active */}
      <EmeraldButton
        className="w-full"
        variant={hasPdf ? "Secondary" : undefined}
        onClick={handleGenerateClick}
        disabled={isLoading}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-3">
            <EmeraldProgressSpinner
              size="small"
              isWhite={false}
              determinate={false}
            />
            Generating PDF link...
          </span>
        ) : (
          generateButtonLabel
        )}
      </EmeraldButton>
      <p className="text-sm text-gray-900">
        {hasPdf
          ? "Generate a new PDF. Any survey updates will require a new link."
          : "Generate and share a PDF link directly with your client."}
      </p>

      {/* Surface generation errors, if any */}
      {status === "error" && (
        <div className="flex text-sm items-start gap-2">
          <EmeraldIcon
            icon="error_outlined"
            variant="icon"
            color="#dc2626"
            className="w-6 h-6"
          />
          <p className="text-red-600">
            {errorMessage || PDF_GENERATION_ERROR_MESSAGE}
          </p>
        </div>
      )}
    </div>
  );
};

export default SharePdfSection;
